"use client";
import {
  ButtonComponent,
  CheckboxComponent,
  DividerComponent,
  LoaderComponent,
  ModalComponent,
  PaperComponent,
} from "@/components";
import { useRouter } from "@/i18n/routing";
import {
  createInsuranceUrl,
  CurrencyConverter,
  GET_PRODUCT_APPLICATION,
  IMAGES,
  PRODUCT_HANDLER,
  PRODUCTS,
  STAGE,
  STAGE_BASED_ROUTING,
  SUBSTAGE,
  WEB_COLORS,
  WEB_EVENTS,
} from "@/lib/constants";
import { useFooter } from "@/lib/context/FooterContext";
import useErrorHandling from "@/lib/hooks/errorhandling";
import { sendEventAnalytics } from "@/lib/hooks/eventAnalytics";
import {
  useCreateLegalityMutation,
  useGetApplicationQuery,
  useGetCustomerQuery,
  useGetESignQuery,
  useGetInsuranceQuery,
  useSelectedOfferTenureMutation,
  useUpdateInsuranceMutation,
} from "@/lib/redux";
import {
  resetPlans,
  setInsuranceDeclaration,
  setSelectedPlans,
} from "@/lib/redux/slice/InsuranceSlice";
import NomineeFields from "@/screens/product-screens/e-sign/insurance-banner/insurance-modal/insurance-nominee";
import {
  Box,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import EmiInsuranceBreakup from "./emi-insurance-breakup";
import { checkForRpl } from "@/lib/utils/rpl.utils";

const notes = [
  "*Your EMI will slightly increase with insurance, but it adds valuable protection.",
  "*The calculation is based on your profile and does not include interest component",
  "*18% GST is included in all insurance plan charges",
];

const ProtectionPlus = () => {
  const router = useRouter();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isBelowMd = useMediaQuery(theme.breakpoints.down("md"));
  const productType = useSelector((s) => s.applicationSlice.productType);
  const [insuranceList, setInsuranceList] = useState([]);
  const [enableEditInsurance, setEnableEditInsurance] = useState(true);
  const [nomineeDetailsModalVisible, setNomineeDetailsModalVisible] =
    useState(false);
  const [dobMismatchModalVisible, setDobMismatchModalVisible] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showBackButton, setShowBackButton] = useState(false);
  const dispatch = useDispatch();
  const { insuranceDeclaration, selectedPlans, insuranceSelected } =
    useSelector((s) => s.insurance);
  const { offerSelectedData } = useSelector((s) => s.offerselection);
  const { setActions } = useFooter();

  const {
    data: applicationData,
    isLoading: fetchingApplications,
    error: applicationFetchError,
    refetch: refetchApplicationData,
  } = useGetApplicationQuery("", { refetchOnMountOrArgChange: true });
  const productApplication = GET_PRODUCT_APPLICATION(
    PRODUCT_HANDLER[productType],
    applicationData?.data,
  );
  const isRPL = checkForRpl(productApplication);
  const { data: customerData, isLoading: fetchingCustomer } =
    useGetCustomerQuery();

  const {
    data: eSignData,
    error: errorESign,
    isLoading: eSignLoading,
    isError: isErrorESign,
    refetch: refetchESignData,
  } = useGetESignQuery(
    { appId: productApplication?.appId },
    {
      skip:
        !productApplication?.appId ||
        productApplication?.stage !== STAGE.E_SIGN,
    },
  );

  const [
    insuranceUpdate,
    {
      data: insuranceUpdateData,
      isLoading: loadingInsuranceUpdateData,
      error: insuranceUpdateDataError,
    },
  ] = useUpdateInsuranceMutation();

  const queryArgs = useMemo(() => {
    if (!productApplication?.appId) return undefined;

    if (
      productApplication?.stage === STAGE.OFFERS &&
      offerSelectedData?.amount &&
      offerSelectedData?.id &&
      offerSelectedData?.tenure
    ) {
      return {
        appId: productApplication.appId,
        params: createInsuranceUrl({
          offerId: offerSelectedData.id,
          newRequestedLoanAmount: `${offerSelectedData.amount}`,
          selectedTenure: offerSelectedData.tenure,
        }),
      };
    }
    return {
      appId: productApplication.appId,
    };
  }, [
    productApplication?.appId,
    productApplication?.stage,
    offerSelectedData?.id,
    offerSelectedData?.amount,
    offerSelectedData?.tenure,
  ]);

  const {
    data: insuranceData,
    isLoading: loadingInsuranceData,
    isFetching: fetchingInsuranceData,
    error: insuranceLoadingError,
  } = useGetInsuranceQuery(queryArgs, {
    skip: !queryArgs,
  });
  // const {
  //   data: insuranceData,
  //   isLoading: loadingInsuranceData,
  //   isFetching: fetchingInsuranceData,
  //   error: insuranceLoadingError,
  // } = useGetInsuranceQuery(queryArgs, {
  //   skip: !shouldFetch || !queryArgs,
  //   refetchOnMountOrArgChange: false,
  // });

  const [
    selectedOfferTenure,
    {
      data: selectedOfferTenureData,
      error: errorSelectedOfferTenure,
      isLoading: selectedOfferTenureLoading,
      isSuccess: successSelectedOfferTenure,
    },
  ] = useSelectedOfferTenureMutation();

  const [
    createLegality,
    {
      data: creatLegalityData,
      isLoading: createLegalityLoading,
      error: errorlegalityDataApi,
      isSuccess: isSuccessLegality,
    },
  ] = useCreateLegalityMutation();
  /***************************************EFFECT************************************ */

  useEffect(() => {
    sendEventAnalytics({
      eventName: WEB_EVENTS.ON_INSURANCE_SCREEN,
      ...productApplication,
      ...customerData?.data,
    });
  }, []);

  useEffect(() => {
    if (productApplication && productApplication?.stage === STAGE.E_SIGN) {
      setShowBackButton(true);
      const nomineeModal = sessionStorage.getItem("nominee_modal");
      const enableInsuranceEdit = JSON.parse(
        sessionStorage.getItem("insurance_editable") ?? "true",
      );
      if (
        eSignData?.data?.insuranceTaken ||
        nomineeModal ||
        !enableInsuranceEdit
      ) {
        setEnableEditInsurance(false);
      }
    } else if (
      productApplication &&
      productApplication?.stage === STAGE.OFFERS
    ) {
      if (!insuranceSelected) {
        setShowBackButton(true);
      } else {
        setShowBackButton(false);
      }
    }
  }, [
    productApplication?.stage,
    insuranceSelected,
    eSignData?.data?.insuranceTaken,
  ]);

  useEffect(() => {
    if (
      productApplication &&
      productApplication?.stage !== STAGE.E_SIGN &&
      productApplication?.stage !== STAGE.OFFERS
    ) {
      setEnableEditInsurance(false);
      setShowBackButton(true);
    } else if (
      productApplication &&
      productApplication?.stage === STAGE.E_SIGN
    ) {
      setShowBackButton(true);
      const nomineeModal = sessionStorage.getItem("nominee_modal");
      if (
        eSignData?.data?.insuranceTaken ||
        nomineeModal ||
        productApplication?.nomineeName ||
        productApplication?.agreementDocumentId ||
        productApplication?.kfsDocumentId
      ) {
        setEnableEditInsurance(false);
      }
    }
  }, [
    productApplication?.stage,
    eSignData?.data?.insuranceTaken,
    productApplication?.nomineeName,
    productApplication?.agreementDocumentId,
    productApplication?.kfsDocumentId,
  ]);

  useEffect(() => {
    if (isSuccessLegality) {
      if (
        creatLegalityData?.data?.reason ===
        SUBSTAGE.UCIC_HUNTER_DECISION_POSTPONED_REJECT
      ) {
        refetchApplicationData();
        return;
      }
      if (creatLegalityData?.data?.signUrl) {
        setShowLoading(true);
        window.location.href = `${creatLegalityData?.data?.signUrl}`;
      } else if (
        creatLegalityData?.data?.subStage ===
          SUBSTAGE?.PAN_DOB_MATCH_DEFERRED ||
        creatLegalityData?.data?.subStage === SUBSTAGE?.PAN_DOB_MISMATCH
      ) {
        setShowLoading(false);
        setDobMismatchModalVisible(true);
      }
    }
  }, [isSuccessLegality]);

  // useEffect(() => {
  //   if (
  //     productApplication &&
  //     insuranceSelected === "" &&
  //     !eSignData?.data?.optionalTotalInsurancePremium
  //   ) {
  //     if (productApplication?.stage !== STAGE.BRE_CPV_TVR) {
  //       router.push(STAGE_BASED_ROUTING(productApplication?.stage));
  //     }
  //     return;
  //   }
  // }, [productApplication?.stage, eSignData, insuranceSelected]);
  useEffect(() => {
    if (!productApplication?.stage) return;

    if (productApplication.stage === STAGE.E_SIGN) {
      if (eSignLoading) return;
      if (!eSignData) return;
    }

    const hasNoInsuranceSelected = insuranceSelected === "";
    const hasNoOptionalPremium =
      !eSignData?.data?.optionalTotalInsurancePremium;

    if (
      hasNoInsuranceSelected &&
      hasNoOptionalPremium &&
      productApplication.stage !== STAGE.BRE_CPV_TVR
    ) {
      router.push(STAGE_BASED_ROUTING(productApplication.stage));
      return;
    }
  }, [
    productApplication?.stage,
    insuranceSelected,
    eSignData,
    eSignLoading,
    router,
  ]);

  useEffect(() => {
    if (productApplication && successSelectedOfferTenure) {
      router.push(STAGE_BASED_ROUTING(productApplication?.stage));
    }
  }, [successSelectedOfferTenure]);

  useEffect(() => {
    if (selectedOfferTenureData?.data?.stage) {
      refetchApplicationData();
    }
  }, [selectedOfferTenureData]);

  useEffect(() => {
    if (insuranceData?.data) {
      setInsuranceList(insuranceData?.data?.insuranceList);
    }
  }, [productApplication?.appId, insuranceData?.data]);

  const isError =
    insuranceUpdateDataError || insuranceLoadingError || applicationFetchError;

  const isLoading =
    fetchingApplications ||
    loadingInsuranceData ||
    fetchingCustomer ||
    showLoading ||
    eSignLoading ||
    loadingInsuranceUpdateData;

  useErrorHandling(isError);

  const goBack = useCallback(() => {
    const enableInsuranceEdit = JSON.parse(
      sessionStorage.getItem("insurance_editable") ?? "false",
    );
    if (!enableInsuranceEdit) {
      sessionStorage.setItem("insurance_editable", JSON.stringify(true));
    }
    router.back();
    return;
  }, [router]);

  useEffect(() => {
    setActions({
      showReject: true,
      showConfirm: showBackButton ? false : true,
      onConfirm: handleWithInsurance,
      confirmBtnLabel: !isMobile ? "Continue with Insurance" : "Continue",
      onReject: !showBackButton ? handleWithoutInsurance : goBack,
      rejectBtnLabel: !showBackButton
        ? !isMobile
          ? "Continue without Insurance"
          : "No, Thanks"
        : "Continue",
      disabledConfirmBtn: insuranceSelected && !insuranceDeclaration,
    });
  }, [
    setActions,
    isMobile,
    selectedPlans,
    enableEditInsurance,
    showBackButton,
    insuranceDeclaration,
    insuranceSelected,
  ]);

  /***************************************ACTION************************************ */

  const handleNomineeAPiRequest = () => {
    setNomineeDetailsModalVisible(false);
    makeLegalityCall();
  };
  const makeLegalityCall = () => {
    sendEventAnalytics({
      eventName: WEB_EVENTS.E_SIGN_STARTED,
      ...productApplication,
      ...customerData?.data,
      sanctionedAmount: eSignData?.data?.loanAmount,
    });
    createLegality({
      appId: productApplication?.appId,
    });
  };
  const handleDobMismatchError = () => {
    setDobMismatchModalVisible(false);
    if (
      creatLegalityData?.data?.subStage === SUBSTAGE?.PAN_DOB_MATCH_DEFERRED
    ) {
      router.push(`/${productType}/e-sign`);
    } else if (
      creatLegalityData?.data?.subStage === SUBSTAGE?.PAN_DOB_MISMATCH
    ) {
      router.push(`/${productType}/offers`);
    }
  };

  const handleWithoutInsurance = useCallback(() => {
    if (productApplication?.stage === STAGE.OFFERS) {
      let insurancePayload = {
        appId: productApplication.appId,
        selectedPlan: [],
        selectOfferRequest: {
          offerId: offerSelectedData?.id || 0,
          newRequestLoanAmount: offerSelectedData?.amount,
          selectedTenure: offerSelectedData?.tenure,
        },
        insuranceConsent: false,
        isConsentRequest: false,
      };
      insuranceUpdate(insurancePayload);
    }
    dispatch(resetPlans());
    router.back();
  }, [productApplication?.stage, dispatch, router]);

  const handleWithInsurance = async () => {
    if (productApplication?.stage === STAGE.OFFERS) {
      let updateOfferTenure = {
        appId: productApplication?.appId,
        offerId: offerSelectedData?.id,
        newRequestLoanAmount: (offerSelectedData?.amount).toString(),
        selectedTenure: parseInt(offerSelectedData?.tenure),
      };
      let insurancePayload = {
        appId: productApplication.appId,
        selectedPlan: selectedPlans,
        selectOfferRequest: {
          offerId: offerSelectedData?.id || 0,
          newRequestLoanAmount: offerSelectedData?.amount,
          selectedTenure: offerSelectedData?.tenure,
        },
        insuranceConsent: true,
        isConsentRequest: true,
      };
      sendEventAnalytics({
        eventName: isRPL
          ? WEB_EVENTS.RL_OFFER_ACCEPTED
          : WEB_EVENTS.SELECTED_LOAN_AMOUNT_AGREE,
        ...productApplication,
        ...customerData?.data,
      });
      sendEventAnalytics({
        eventName: WEB_EVENTS.OFFER_SCREEN_INSURANCE_OPTED,
        ...productApplication,
        ...customerData?.data,
      });
      try {
        await insuranceUpdate(insurancePayload).unwrap();

        await selectedOfferTenure({ body: updateOfferTenure, isRPL }).unwrap();
      } catch (err) {
        console.error("API Error:", err);
        return;
      }
    } else if (productApplication?.stage === STAGE.E_SIGN) {
      sendEventAnalytics({
        eventName: WEB_EVENTS.INSURANCE_OPTED_FINAL,
        ...productApplication,
        ...customerData?.data,
      });
      if (
        ((productApplication?.agreementDocumentId ||
          productApplication?.kfsDocumentId) &&
          !productApplication?.nomineeName) ||
        productApplication?.nomineeName
      ) {
        makeLegalityCall();
        return;
      }
      if (
        !productApplication?.nomineeName &&
        !productApplication?.agreementDocumentId &&
        !productApplication?.kfsDocumentId
      ) {
        setNomineeDetailsModalVisible(true);
      } else {
        makeLegalityCall();
      }
    }
  };

  const makeInsuranceUpdatePayload = useCallback(
    (newSelectedPlans) => {
      if (!productApplication?.appId) return null;

      return {
        appId: productApplication.appId,
        selectedPlan: newSelectedPlans,
        selectOfferRequest: {
          offerId:
            productApplication?.stage === STAGE.OFFERS
              ? offerSelectedData?.id || 0
              : 0,
          newRequestLoanAmount:
            productApplication?.stage === STAGE.OFFERS
              ? offerSelectedData?.amount
              : 0,
          selectedTenure:
            productApplication?.stage === STAGE.OFFERS
              ? offerSelectedData?.tenure
              : 0,
        },
        insuranceConsent: false,
        isConsentRequest: false,
      };
    },
    [
      productApplication?.appId,
      productApplication?.stage,
      offerSelectedData?.id,
      offerSelectedData?.amount,
      offerSelectedData?.tenure,
      eSignData?.data?.loanAmount,
      eSignData?.data?.repaymentTime,
      eSignData?.data?.insuranceTaken,
    ],
  );

  const handleInsuranceCheckbox = useCallback(
    (id) => {
      const activePlans = insuranceList
        ?.filter((insurance) => insurance?.isActive)
        .map((item) => item?.id);
      const newSelectedPlanArray = activePlans?.includes(id)
        ? activePlans?.filter((val) => val !== id)
        : [...activePlans, id];

      dispatch(setSelectedPlans(newSelectedPlanArray));

      const payload = makeInsuranceUpdatePayload(newSelectedPlanArray);

      if (payload) {
        insuranceUpdate(payload);
      }
    },
    [
      selectedPlans,
      dispatch,
      makeInsuranceUpdatePayload,
      insuranceUpdate,
      insuranceList,
    ],
  );

  if (isLoading) return <LoaderComponent open={isLoading || showLoading} />;

  return (
    <>
      <LoaderComponent open={isLoading || showLoading} />
      {!!insuranceData && (
        <>
          <PaperComponent
            noDrop
            backgroundColor={WEB_COLORS.darkTeal}
            paperStyle={{ color: "#fff", backgroundColor: WEB_COLORS.darkTeal }}
            p={2}
            borderRadius={"24px"}
            mb={{ xs: 2, md: 8 }}
          >
            <Box
              display={"flex"}
              sx={{
                flexDirection: { xs: "column", md: "row" },
                alignItems: { md: "center" },
              }}
              gap={2}
              mb={2}
            >
              <Image
                src={IMAGES.surakshaIconLight}
                alt="suraksha-icon"
                width={isMobile ? 60 : "100%"}
              />

              <Box>
                <Typography
                  fontWeight={{ xs: 500 }}
                  fontSize={{ xs: "24px", md: "32px" }}
                  lineHeight={"40px"}
                >
                  Protection Plus
                </Typography>
                <Typography fontWeight={400} fontSize={"14px"}>
                  One Plan. Triple Protection.
                </Typography>
              </Box>
            </Box>
            {!isBelowMd && <DividerComponent color={"#CCD6D4"} />}
            <Stack mt={2}>
              <Typography
                fontWeight={{ xs: 350, md: 400 }}
                fontSize={{ xs: "12px", md: "14px" }}
              >
                Safeguard your loved ones with a lump sum payout, EMI protection
                and healthcare benefits for added peace of mind.
              </Typography>
              <Typography
                mt={1}
                fontWeight={500}
                fontSize={{ xs: "14px", md: "20px" }}
              >
                Get all three for just{" "}
                {CurrencyConverter(insuranceData?.data?.allInsurance)} per
                month*
              </Typography>
            </Stack>
            <Stack flexDirection={"row"} alignItems={"start"} gap={1} mt={2}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
                width="20"
                height="20"
              >
                <path
                  fill="white"
                  d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336l24 0 0-64-24 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l48 0c13.3 0 24 10.7 24 24l0 88 8 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-80 0c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-208a32 32 0 1 1 0 64 32 32 0 1 1 0-64z"
                />
              </svg>
              <Typography
                fontWeight={{ md: 500, xs: 400 }}
                fontSize={{ md: "14px", xs: "10px" }}
              >
                Insurance premium doesn’t affect your loan amount- It’s offered
                in addition to your disbursal.
              </Typography>
            </Stack>
          </PaperComponent>
          {insuranceList?.map((insurance, index) => (
            <PaperComponent
              noDrop
              key={insurance.title}
              paperStyle={{
                backgroundColor: "#F2F7F799",
                px: { xs: 2, md: 3 },
                py: { xs: 2, md: 3 },
              }}
            >
              <Stack
                flexDirection={"row"}
                justifyContent={"space-between"}
                alignItems={"flex-start"}
                // gap={2}
                color={WEB_COLORS.darkTeal}
              >
                <Box
                  display={"flex"}
                  sx={{
                    flexDirection: { xs: "column", md: "row" },
                  }}
                >
                  <CheckboxComponent
                    name={`insurance-${index}`}
                    checkboxProps={{
                      size: isMobile ? "extra-small" : "small",
                    }}
                    checked={insurance?.isActive}
                    disabled={!enableEditInsurance}
                    onChange={() => handleInsuranceCheckbox(insurance?.id)}
                    label={insurance?.title}
                  />

                  <Box
                    display={"flex"}
                    gap={1}
                    alignItems={"center"}
                    ml={{ xs: 3, md: 0 }}
                    mt={{ xs: 1, md: 0 }}
                  >
                    <Image src={IMAGES.calendarIcon} alt="validity" />
                    <Typography
                      fontWeight={500}
                      fontSize={{ xs: "10px", md: "14px" }}
                    >
                      Validity
                    </Typography>
                    <Typography
                      fontWeight={350}
                      fontSize={{ xs: "10px", md: "14px" }}
                    >
                      : {insurance?.validity} Months
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography
                    sx={{ textDecorationLine: "underline", cursor: "pointer" }}
                    fontWeight={500}
                    fontSize={{ xs: "10px", md: "14px" }}
                    onClick={() =>
                      window.open(
                        insurance?.policyUrl,
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                  >
                    View Policy
                  </Typography>
                </Box>
              </Stack>
              <Grid container justifyContent="space-between" spacing={2} mt={1}>
                {insurance?.benefits?.map((benefit, index) => (
                  <Grid key={index} item xs={6} md={3}>
                    <Grid
                      container
                      flexDirection={{ xs: "row", md: "column" }}
                      alignItems={{ xs: "center", md: "center" }}
                      justifyContent={{ md: "center" }}
                      columnGap={1}
                      rowGap={1}
                      wrap="nowrap"
                      sx={{
                        bgcolor: { md: "#E5F1F1" },
                        p: { md: 2 },
                        borderRadius: { sm: "20px" },
                        height: { xs: "100%", md: "195px" },
                        width: "100%",
                        color: "#004135",
                      }}
                    >
                      <Grid item>
                        <Image
                          src={benefit.logoUrl}
                          alt={`icon-${index}`}
                          width={isMobile ? 30 : 40}
                          height={isMobile ? 30 : 40}
                        />
                      </Grid>

                      <Grid item textAlign={{ md: "center", xs: "left" }}>
                        <Typography
                          fontWeight={500}
                          fontSize={{ xs: "12px", md: "16px" }}
                        >
                          {benefit?.title}
                        </Typography>
                        <Typography
                          fontWeight={350}
                          fontSize={{ xs: "10px", md: "14px" }}
                        >
                          {benefit?.description}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                ))}
              </Grid>

              <DividerComponent sx={{ borderStyle: "dashed", mt: 2 }} />
              <Stack mt={2}>
                <Typography
                  fontWeight={400}
                  fontSize={{ xs: "12px", md: "14px" }}
                  color={"#4D6767"}
                >
                  "{insurance?.slogan}"
                </Typography>
              </Stack>
              <Box
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                mt={1}
              >
                <Image
                  src={insurance.logoUrl}
                  alt={"insurance-icon"}
                  width={isMobile ? 50 : 100}
                  height={isMobile ? 16 : 50}
                />
                <Typography
                  fontWeight={700}
                  fontSize={{ xs: "12px", md: "20px" }}
                  color={WEB_COLORS.darkTeal}
                >
                  {CurrencyConverter(insurance?.amount, 2)}/{insurance?.unit}*
                </Typography>
              </Box>
            </PaperComponent>
          ))}
          <Stack mx={1}>
            {notes?.map((note, index) => (
              <Typography
                key={index}
                fontWeight={{ xs: 350, md: 400 }}
                fontSize={{ xs: "10px", md: "14px" }}
                color={"#4D6767CC"}
                fontStyle={{ md: "normal", xs: "italic" }}
              >
                {note}
              </Typography>
            ))}
          </Stack>
          <EmiInsuranceBreakup insuranceData={insuranceData?.data || {}} />

          {productApplication?.stage !== STAGE.E_SIGN && insuranceSelected && (
            <Stack>
              <CheckboxComponent
                name="consent-check"
                checkboxProps={{
                  disabled: !insuranceSelected,
                }}
                label={
                  <Typography
                    fontWeight={{ md: 350, xs: 400 }}
                    fontSize={{ xs: "10px", md: "14px" }}
                  >
                    I certify that by selecting Insurance the proposed member is
                    in good health, free from mental or physical impairment & do
                    not suffer from any chronic or pre-existing disease.
                  </Typography>
                }
                checked={insuranceDeclaration}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  dispatch(setInsuranceDeclaration(isChecked));
                }}
              />
            </Stack>
          )}
        </>
      )}
      <NomineeFields
        open={nomineeDetailsModalVisible}
        productApplication={productApplication}
        handleModal={setNomineeDetailsModalVisible}
        handleOnProceed={handleNomineeAPiRequest}
      />
      <ModalComponent
        closeButtonRequired={false}
        open={dobMismatchModalVisible}
      >
        <Stack alignItems={"center"} justifyContent={"center"} gap={4}>
          <Typography variant="h6">{creatLegalityData?.data?.label}</Typography>
          <Typography variant="h7">
            {creatLegalityData?.data?.message}
          </Typography>
          <ButtonComponent onClick={handleDobMismatchError}>Ok</ButtonComponent>
        </Stack>
      </ModalComponent>
    </>
  );
};

export default ProtectionPlus;
